import { useRef, useState } from "react";
import {
  changeSchema,
  DatasetError,
  previewSchemaChange,
  type DatasetDescription,
  type DatasetField,
  type DatasetFieldSpec,
  type DatasetOption,
  type DatasetSchemaImpact,
} from "@/api/datasets";
import { translate } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { useNotificationStore } from "@/stores/notification-store";
import {
  renamedField,
  withField,
  withoutField,
  withoutOption,
} from "./dataset-field-specs";

/** The open field dialog; `confirm` holds a previewed change that deletes data. */
export type DatasetFieldDialog =
  | { kind: "rename"; field: DatasetField }
  | { kind: "add" }
  | {
      kind: "confirm";
      field: DatasetField;
      option: DatasetOption | null;
      impact: DatasetSchemaImpact;
      fields: DatasetFieldSpec[];
      /** The revision the preview read: Confirm applies to that one only. */
      schemaRevision: number;
    };

/**
 * A person's field changes to an opened Data frame. Rename and Add field
 * apply at once. Deleting a field or a select option first asks the schema
 * preview what it deletes, then waits in a `confirm` dialog: only Confirm
 * sends `confirm_destructive`, with the revision the preview read, so a
 * change made meanwhile is refused rather than overwritten. A refusal is a
 * toast, and a stale revision reloads the fields.
 */
export function useDatasetFields({
  projectId,
  frameId,
  fields,
  schemaRevision,
  onChanged,
  onStale,
}: {
  projectId: string | null;
  frameId: string;
  fields: readonly DatasetField[];
  schemaRevision: number;
  onChanged: (description: DatasetDescription) => void;
  onStale: () => void;
}) {
  const [dialog, setDialog] = useState<DatasetFieldDialog | null>(null);
  const [busy, setBusy] = useState(false);
  // Each dialog shown or closed outdates a preview still on its way.
  const shown = useRef(0);
  const show = (next: DatasetFieldDialog | null) => {
    shown.current += 1;
    setDialog(next);
  };

  const refused = (error: unknown) => {
    const refusal =
      error instanceof DatasetError ? error : new DatasetError(error);
    const stale = refusal.code === "schema_conflict";
    useNotificationStore.getState().addSystemToast({
      id: `dataset-fields-${frameId}`,
      level: "error",
      title: translate(
        framesDatasetMessages,
        stale ? "fieldsChanged" : "fieldChangeFailed",
      ),
      description: refusal.code === "invalid" ? refusal.message : undefined,
    });
    if (stale) onStale();
  };

  const apply = async (
    next: DatasetFieldSpec[],
    revision: number,
    confirmed = false,
  ) => {
    if (!projectId) return;
    setBusy(true);
    try {
      onChanged(
        await changeSchema(projectId, frameId, {
          fields: next,
          expected_schema_revision: revision,
          ...(confirmed ? { confirm_destructive: true as const } : {}),
        }),
      );
    } catch (error) {
      refused(error);
    } finally {
      setBusy(false);
      show(null);
    }
  };

  const askFirst = async (
    next: DatasetFieldSpec[],
    field: DatasetField,
    option: DatasetOption | null,
  ) => {
    if (!projectId) return;
    const mine = ++shown.current;
    const revision = schemaRevision;
    try {
      const impact = await previewSchemaChange(projectId, frameId, next);
      if (mine !== shown.current) return;
      show({
        kind: "confirm",
        field,
        option,
        impact,
        fields: next,
        schemaRevision: revision,
      });
    } catch (error) {
      if (mine === shown.current) refused(error);
    }
  };

  return {
    dialog,
    busy,
    startRename: (field: DatasetField) => show({ kind: "rename", field }),
    startAdd: () => show({ kind: "add" }),
    close: () => show(null),
    rename: (fieldId: string, name: string) =>
      apply(renamedField(fields, fieldId, name), schemaRevision),
    add: (spec: DatasetFieldSpec) =>
      apply(withField(fields, spec), schemaRevision),
    deleteField: (field: DatasetField) =>
      void askFirst(withoutField(fields, field.id), field, null),
    removeOption: (field: DatasetField, option: DatasetOption) =>
      void askFirst(withoutOption(fields, field.id, option.id), field, option),
    confirm: () => {
      if (dialog?.kind === "confirm") {
        void apply(dialog.fields, dialog.schemaRevision, true);
      }
    },
  };
}

export type DatasetFields = ReturnType<typeof useDatasetFields>;
